import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Smartphone, Code, Database, FileText, Settings, CheckCircle, ArrowRight, ChevronDown, Layers, Briefcase } from 'lucide-react';
import Impressum from './Impressum'; // Add this import

const Infrastruktur = () => {
  const [scrolled, setScrolled] = useState(false);
  const [currentLang, setCurrentLang] = useState('de');
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [showImpressum, setShowImpressum] = useState(false); // Add this state

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const translations = {
    de: {
      nav: {
        home: "Startseite",
        demo: "Demo anfordern"
      },
      hero: {
        title: "Digitale Infrastruktur",
        subtitle: "Maßgeschneiderte Digitalisierung für moderne Agrarbetriebe",
        description: "Von Smart Contracts über Apps bis zur EU-Förderberatung - wir digitalisieren Ihre Landwirtschaft"
      },
      services: {
        title: "Unsere Digitalisierungsleistungen",
        items: [
          {
            title: "Webplattformen",
            desc: "Individuelle Online-Portale für Ihre Betriebsverwaltung",
            features: [
              "Responsive Design für alle Geräte",
              "Echtzeit-Datenvisualisierung",
              "Sichere Cloud-Speicherung",
              "API-Integration"
            ]
          },
          {
            title: "Mobile Apps",
            desc: "Native Apps für iOS und Android zur Feldverwaltung",
            features: [
              "Offline-Funktionalität",
              "GPS-basierte Felderfassung",
              "Foto-Dokumentation",
              "Push-Benachrichtigungen"
            ]
          },
          {
            title: "Smart Contracts",
            desc: "Automatisierte Verträge auf der Blockchain",
            features: [
              "Automatische Zahlungsabwicklung",
              "Transparente Bedingungen",
              "Kosteneinsparung",
              "Rechtssicherheit"
            ]
          },
          {
            title: "EU-Förderberatung",
            desc: "Digitale Tools für optimale Fördernutzung",
            features: [
              "Automatische Förderprüfung",
              "Antragsassistent",
              "Deadline-Management",
              "Compliance-Tracking"
            ]
          }
        ]
      },
      solutions: {
        title: "Komplettlösungen für jeden Bedarf",
        subtitle: "Wir entwickeln ganzheitliche Systeme, die alle Aspekte Ihres Betriebs abdecken",
        items: [
          {
            title: "Betriebsmanagement-System",
            desc: "Zentrale Verwaltung aller Betriebsprozesse",
            icon: "management"
          },
          {
            title: "Precision Farming Tools",
            desc: "Präzise Steuerung von Aussaat, Düngung und Ernte",
            icon: "precision"
          },
          {
            title: "Supply Chain Integration",
            desc: "Nahtlose Verbindung zu Lieferanten und Abnehmern",
            icon: "supply"
          },
          {
            title: "Datenanalyse-Dashboard",
            desc: "Visualisierung und Auswertung aller Betriebsdaten",
            icon: "analytics"
          }
        ]
      },
      process: {
        title: "Unser Digitalisierungsprozess",
        steps: [
          {
            title: "Bedarfsanalyse",
            desc: "Gemeinsame Erfassung Ihrer spezifischen Anforderungen"
          },
          {
            title: "Konzeptentwicklung",
            desc: "Maßgeschneidertes Digitalisierungskonzept für Ihren Betrieb"
          },
          {
            title: "Implementierung",
            desc: "Schrittweise Umsetzung mit kontinuierlicher Abstimmung"
          },
          {
            title: "Schulung & Support",
            desc: "Umfassende Einweisung und dauerhafter Support"
          }
        ]
      },
      technologies: {
        title: "Modernste Technologien",
        items: [
          "React & React Native für Web und Mobile",
          "Blockchain (Ethereum, Polygon)",
          "Cloud Computing (AWS, Azure)",
          "IoT-Integration für Sensoren",
          "Machine Learning APIs",
          "RESTful & GraphQL APIs"
        ]
      },
      benefits: {
        title: "Vorteile der Digitalisierung",
        items: [
          {
            title: "Effizienzsteigerung",
            desc: "Bis zu 40% Zeitersparnis bei administrativen Aufgaben"
          },
          {
            title: "Kostensenkung",
            desc: "Reduzierung der Betriebskosten durch optimierte Prozesse"
          },
          {
            title: "Bessere Entscheidungen",
            desc: "Datenbasierte Insights für fundierte Betriebsentscheidungen"
          },
          {
            title: "Zukunftssicherheit",
            desc: "Vorbereitung auf kommende regulatorische Anforderungen"
          }
        ]
      },
      cta: {
        title: "Starten Sie Ihre digitale Transformation",
        subtitle: "Gemeinsam machen wir Ihren Betrieb fit für die Zukunft",
        button: "Kostenloses Beratungsgespräch"
      }
    },
    en: {
      nav: {
        home: "Home",
        demo: "Request Demo"
      },
      hero: {
        title: "Digital Infrastructure",
        subtitle: "Tailored digitalization for modern farms",
        description: "From smart contracts to apps to EU funding advice - we digitize your agriculture"
      },
      services: {
        title: "Our Digitalization Services",
        items: [
          {
            title: "Web Platforms",
            desc: "Custom online portals for your farm management",
            features: [
              "Responsive design for all devices",
              "Real-time data visualization",
              "Secure cloud storage",
              "API integration"
            ]
          },
          {
            title: "Mobile Apps",
            desc: "Native apps for iOS and Android for field management",
            features: [
              "Offline functionality",
              "GPS-based field capture",
              "Photo documentation",
              "Push notifications"
            ]
          },
          {
            title: "Smart Contracts",
            desc: "Automated contracts on the blockchain",
            features: [
              "Automatic payment processing",
              "Transparent conditions",
              "Cost savings",
              "Legal certainty"
            ]
          },
          {
            title: "EU Funding Advice",
            desc: "Digital tools for optimal funding utilization",
            features: [
              "Automatic funding check",
              "Application assistant",
              "Deadline management",
              "Compliance tracking"
            ]
          }
        ]
      },
      solutions: {
        title: "Complete Solutions for Every Need",
        subtitle: "We develop holistic systems that cover all aspects of your operation",
        items: [
          {
            title: "Farm Management System",
            desc: "Central management of all operational processes",
            icon: "management"
          },
          {
            title: "Precision Farming Tools",
            desc: "Precise control of seeding, fertilization and harvest",
            icon: "precision"
          },
          {
            title: "Supply Chain Integration",
            desc: "Seamless connection to suppliers and buyers",
            icon: "supply"
          },
          {
            title: "Data Analysis Dashboard",
            desc: "Visualization and evaluation of all operational data",
            icon: "analytics"
          }
        ]
      },
      process: {
        title: "Our Digitalization Process",
        steps: [
          {
            title: "Needs Analysis",
            desc: "Joint assessment of your specific requirements"
          },
          {
            title: "Concept Development",
            desc: "Tailored digitalization concept for your operation"
          },
          {
            title: "Implementation",
            desc: "Step-by-step implementation with continuous coordination"
          },
          {
            title: "Training & Support",
            desc: "Comprehensive introduction and permanent support"
          }
        ]
      },
      technologies: {
        title: "State-of-the-Art Technologies",
        items: [
          "React & React Native for web and mobile",
          "Blockchain (Ethereum, Polygon)",
          "Cloud Computing (AWS, Azure)",
          "IoT integration for sensors",
          "Machine Learning APIs",
          "RESTful & GraphQL APIs"
        ]
      },
      benefits: {
        title: "Benefits of Digitalization",
        items: [
          {
            title: "Efficiency Increase",
            desc: "Up to 40% time savings on administrative tasks"
          },
          {
            title: "Cost Reduction",
            desc: "Reduction of operating costs through optimized processes"
          },
          {
            title: "Better Decisions",
            desc: "Data-based insights for informed operational decisions"
          },
          {
            title: "Future Security",
            desc: "Preparation for upcoming regulatory requirements"
          }
        ]
      },
      cta: {
        title: "Start Your Digital Transformation",
        subtitle: "Together we make your operation fit for the future",
        button: "Free Consultation"
      }
    },
    it: {
      nav: {
        home: "Home",
        demo: "Richiedi Demo"
      },
      hero: {
        title: "Infrastruttura Digitale",
        subtitle: "Digitalizzazione su misura per aziende agricole moderne",
        description: "Da smart contract ad app a consulenza sui fondi UE - digitalizziamo la tua agricoltura"
      },
      services: {
        title: "I Nostri Servizi di Digitalizzazione",
        items: [
          {
            title: "Piattaforme Web",
            desc: "Portali online personalizzati per la gestione aziendale",
            features: [
              "Design responsive per tutti i dispositivi",
              "Visualizzazione dati in tempo reale",
              "Archiviazione cloud sicura",
              "Integrazione API"
            ]
          },
          {
            title: "App Mobile",
            desc: "App native per iOS e Android per la gestione dei campi",
            features: [
              "Funzionalità offline",
              "Acquisizione campi basata su GPS",
              "Documentazione fotografica",
              "Notifiche push"
            ]
          },
          {
            title: "Smart Contract",
            desc: "Contratti automatizzati sulla blockchain",
            features: [
              "Elaborazione pagamenti automatica",
              "Condizioni trasparenti",
              "Risparmio sui costi",
              "Certezza legale"
            ]
          },
          {
            title: "Consulenza Fondi UE",
            desc: "Strumenti digitali per l'utilizzo ottimale dei finanziamenti",
            features: [
              "Controllo automatico dei finanziamenti",
              "Assistente per le domande",
              "Gestione scadenze",
              "Monitoraggio conformità"
            ]
          }
        ]
      },
      solutions: {
        title: "Soluzioni Complete per Ogni Esigenza",
        subtitle: "Sviluppiamo sistemi olistici che coprono tutti gli aspetti della tua operazione",
        items: [
          {
            title: "Sistema di Gestione Aziendale",
            desc: "Gestione centrale di tutti i processi operativi",
            icon: "management"
          },
          {
            title: "Strumenti di Precision Farming",
            desc: "Controllo preciso di semina, fertilizzazione e raccolta",
            icon: "precision"
          },
          {
            title: "Integrazione Supply Chain",
            desc: "Connessione senza soluzione di continuità con fornitori e acquirenti",
            icon: "supply"
          },
          {
            title: "Dashboard Analisi Dati",
            desc: "Visualizzazione e valutazione di tutti i dati operativi",
            icon: "analytics"
          }
        ]
      },
      process: {
        title: "Il Nostro Processo di Digitalizzazione",
        steps: [
          {
            title: "Analisi dei Bisogni",
            desc: "Valutazione congiunta dei tuoi requisiti specifici"
          },
          {
            title: "Sviluppo del Concetto",
            desc: "Concetto di digitalizzazione su misura per la tua operazione"
          },
          {
            title: "Implementazione",
            desc: "Implementazione graduale con coordinamento continuo"
          },
          {
            title: "Formazione e Supporto",
            desc: "Introduzione completa e supporto permanente"
          }
        ]
      },
      technologies: {
        title: "Tecnologie All'Avanguardia",
        items: [
          "React e React Native per web e mobile",
          "Blockchain (Ethereum, Polygon)",
          "Cloud Computing (AWS, Azure)",
          "Integrazione IoT per sensori",
          "API di Machine Learning",
          "API RESTful e GraphQL"
        ]
      },
      benefits: {
        title: "Vantaggi della Digitalizzazione",
        items: [
          {
            title: "Aumento dell'Efficienza",
            desc: "Fino al 40% di risparmio di tempo nelle attività amministrative"
          },
          {
            title: "Riduzione dei Costi",
            desc: "Riduzione dei costi operativi attraverso processi ottimizzati"
          },
          {
            title: "Decisioni Migliori",
            desc: "Insights basati sui dati per decisioni operative informate"
          },
          {
            title: "Sicurezza Futura",
            desc: "Preparazione per i prossimi requisiti normativi"
          }
        ]
      },
      cta: {
        title: "Inizia la Tua Trasformazione Digitale",
        subtitle: "Insieme rendiamo la tua azienda pronta per il futuro",
        button: "Consulenza Gratuita"
      }
    },
    es: {
      nav: {
        home: "Inicio",
        demo: "Solicitar Demo"
      },
      hero: {
        title: "Infraestructura Digital",
        subtitle: "Digitalización a medida para granjas modernas",
        description: "Desde contratos inteligentes hasta aplicaciones y asesoramiento sobre fondos de la UE: digitalizamos su agricultura"
      },
      services: {
        title: "Nuestros Servicios de Digitalización",
        items: [
          {
            title: "Plataformas Web",
            desc: "Portales en línea personalizados para su gestión agrícola",
            features: [
              "Diseño responsive para todos los dispositivos",
              "Visualización de datos en tiempo real",
              "Almacenamiento seguro en la nube",
              "Integración de API"
            ]
          },
          {
            title: "Aplicaciones Móviles",
            desc: "Aplicaciones nativas para iOS y Android para gestión de campos",
            features: [
              "Funcionalidad sin conexión",
              "Captura de campo basada en GPS",
              "Documentación fotográfica",
              "Notificaciones push"
            ]
          },
          {
            title: "Contratos Inteligentes",
            desc: "Contratos automatizados en blockchain",
            features: [
              "Procesamiento automático de pagos",
              "Condiciones transparentes",
              "Ahorro de costos",
              "Seguridad jurídica"
            ]
          },
          {
            title: "Asesoramiento Fondos UE",
            desc: "Herramientas digitales para el uso óptimo de la financiación",
            features: [
              "Verificación automática de financiación",
              "Asistente de solicitud",
              "Gestión de plazos",
              "Seguimiento de cumplimiento"
            ]
          }
        ]
      },
      solutions: {
        title: "Soluciones Completas para Cada Necesidad",
        subtitle: "Desarrollamos sistemas holísticos que cubren todos los aspectos de su operación",
        items: [
          {
            title: "Sistema de Gestión Agrícola",
            desc: "Gestión central de todos los procesos operativos",
            icon: "management"
          },
          {
            title: "Herramientas de Agricultura de Precisión",
            desc: "Control preciso de siembra, fertilización y cosecha",
            icon: "precision"
          },
          {
            title: "Integración de Cadena de Suministro",
            desc: "Conexión perfecta con proveedores y compradores",
            icon: "supply"
          },
          {
            title: "Panel de Análisis de Datos",
            desc: "Visualización y evaluación de todos los datos operativos",
            icon: "analytics"
          }
        ]
      },
      process: {
        title: "Nuestro Proceso de Digitalización",
        steps: [
          {
            title: "Análisis de Necesidades",
            desc: "Evaluación conjunta de sus requisitos específicos"
          },
          {
            title: "Desarrollo del Concepto",
            desc: "Concepto de digitalización a medida para su operación"
          },
          {
            title: "Implementación",
            desc: "Implementación paso a paso con coordinación continua"
          },
          {
            title: "Formación y Soporte",
            desc: "Introducción integral y soporte permanente"
          }
        ]
      },
      technologies: {
        title: "Tecnologías de Vanguardia",
        items: [
          "React y React Native para web y móvil",
          "Blockchain (Ethereum, Polygon)",
          "Cloud Computing (AWS, Azure)",
          "Integración IoT para sensores",
          "APIs de Machine Learning",
          "APIs RESTful y GraphQL"
        ]
      },
      benefits: {
        title: "Beneficios de la Digitalización",
        items: [
          {
            title: "Aumento de Eficiencia",
            desc: "Hasta un 40% de ahorro de tiempo en tareas administrativas"
          },
          {
            title: "Reducción de Costos",
            desc: "Reducción de costos operativos mediante procesos optimizados"
          },
          {
            title: "Mejores Decisiones",
            desc: "Información basada en datos para decisiones operativas informadas"
          },
          {
            title: "Seguridad Futura",
            desc: "Preparación para los próximos requisitos regulatorios"
          }
        ]
      },
      cta: {
        title: "Comience su Transformación Digital",
        subtitle: "Juntos hacemos que su operación esté preparada para el futuro",
        button: "Consulta Gratuita"
      }
    }
  };

  const t = translations[currentLang];

  const languages = [
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ];

  const ZebraLogo = () => (
    <img 
      src="/logo.png" 
      alt="Lazzaro Logo" 
      className="w-full h-full object-contain"
    />
  );

  const getSolutionIcon = (iconType) => {
    switch(iconType) {
      case 'management': return <Briefcase className="w-8 h-8" />;
      case 'precision': return <Settings className="w-8 h-8" />;
      case 'supply': return <Layers className="w-8 h-8" />;
      case 'analytics': return <Database className="w-8 h-8" />;
      default: return <Globe className="w-8 h-8" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-gray-900/95 backdrop-blur-xl shadow-2xl' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-12 h-12 relative">
                <ZebraLogo />
              </div>
              <div>
                <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-amber-500 bg-clip-text text-transparent">Lazzaro1</span>
                <p className="text-xs text-amber-400/80 font-medium">SMART DATA FOR SMARTER FARMING</p>
              </div>
            </Link>
            
            <div className="flex items-center space-x-8">
              <Link to="/" className="hover:text-cyan-400 transition-colors">{t.nav.home}</Link>
              
              {/* Language Selector */}
              <div className="relative">
                <button
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                  className="flex items-center space-x-2 hover:text-cyan-400 transition-colors"
                >
                  <span>{languages.find(l => l.code === currentLang)?.flag}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {langMenuOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-gray-900 border border-gray-700 rounded-lg shadow-xl">
                    {languages.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setCurrentLang(lang.code);
                          setLangMenuOpen(false);
                        }}
                        className={`w-full px-4 py-2 text-left hover:bg-gray-800 transition-colors flex items-center space-x-2 ${
                          currentLang === lang.code ? 'text-cyan-400' : ''
                        }`}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <button className="bg-gradient-to-r from-cyan-500 to-amber-600 px-6 py-2 rounded-full hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 transform hover:-translate-y-0.5">
                {t.nav.demo}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-900/20 via-transparent to-amber-900/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 text-white mb-6">
              <Globe className="w-16 h-16" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-orange-400 to-amber-500 bg-clip-text text-transparent">
              {t.hero.title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-4 max-w-3xl mx-auto">
              {t.hero.subtitle}
            </p>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              {t.hero.description}
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">{t.services.title}</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {t.services.items.map((service, index) => (
              <div key={index} className="p-8 rounded-xl bg-gray-800/50 hover:bg-gray-800 transition-all duration-300">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="p-3 rounded-lg bg-gradient-to-br from-orange-500/20 to-amber-600/20 text-orange-400">
                    {index === 0 && <Globe className="w-8 h-8" />}
                    {index === 1 && <Smartphone className="w-8 h-8" />}
                    {index === 2 && <Code className="w-8 h-8" />}
                    {index === 3 && <FileText className="w-8 h-8" />}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-semibold mb-2">{service.title}</h3>
                    <p className="text-gray-400 mb-4">{service.desc}</p>
                  </div>
                </div>
                <div className="space-y-2 ml-14">
                  {service.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{t.solutions.title}</h2>
            <p className="text-xl text-gray-400">{t.solutions.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.solutions.items.map((solution, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900">
                <div className="inline-flex p-3 rounded-lg bg-gradient-to-br from-orange-500/20 to-amber-600/20 text-orange-400 mb-4">
                  {getSolutionIcon(solution.icon)}
                </div>
                <h3 className="text-xl font-semibold mb-2">{solution.title}</h3>
                <p className="text-gray-400">{solution.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">{t.process.title}</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {t.process.steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 rounded-full flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-gray-400">{step.desc}</p>
                </div>
                {index < t.process.steps.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-8 -right-4 w-8 h-8 text-gray-600" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">{t.technologies.title}</h2>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-4">
              {t.technologies.items.map((tech, index) => (
                <div key={index} className="flex items-center space-x-3 p-4 rounded-lg bg-gray-800/50">
                  <Code className="w-6 h-6 text-orange-400 flex-shrink-0" />
                  <p className="text-lg">{tech}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">{t.benefits.title}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.benefits.items.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold bg-gradient-to-r from-orange-400 to-amber-500 bg-clip-text text-transparent mb-4">
                  {index === 0 && "40%"}
                  {index === 1 && "30%"}
                  {index === 2 && "100%"}
                  {index === 3 && "✓"}
                </div>
                <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                <p className="text-gray-400">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-900/20 to-amber-900/20"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t.cta.title}
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            {t.cta.subtitle}
          </p>
          <button className="group bg-gradient-to-r from-orange-500 to-amber-600 px-8 py-4 rounded-full text-lg font-semibold hover:shadow-2xl hover:shadow-orange-500/25 transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-center mx-auto">
            {t.cta.button}
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* Footer - modified to add Impressum */}
      <footer className="bg-gray-900 py-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Link to="/" className="inline-flex items-center space-x-3 mb-4">
              <div className="w-10 h-10">
                <ZebraLogo />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-amber-500 bg-clip-text text-transparent">Lazzaro1</span>
            </Link>
            <p className="text-gray-400">© 2024 Lazzaro One. Alle Rechte vorbehalten.</p>
            
            {/* Add Impressum Link */}
            <div className="mt-4">
              <button
                onClick={() => setShowImpressum(true)}
                className="text-gray-400 hover:text-cyan-400 transition-colors text-sm"
              >
                Impressum
              </button>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Add Impressum Modal */}
      <Impressum isOpen={showImpressum} onClose={() => setShowImpressum(false)} />
    </div>
  );
};

export default Infrastruktur;